import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { InfoIcon } from 'lucide-react';
import { type useCloneTemplates } from '@/features/quote-and-apply-builder/hooks/use-clone-templates';

type CloneTemplatesDialogProps = ReturnType<typeof useCloneTemplates>;

export function CloneTemplatesDialog({
  sourceForm,
  destinationFormId,
  setDestinationFormId,
  destinationForms,
  isCloning,
  isCloneDialogOpen,
  closeCloneDialog,
  cloneTemplates,
}: CloneTemplatesDialogProps) {
  const hasDestinations = destinationForms.length > 0;

  return (
    <Dialog
      open={isCloneDialogOpen}
      onOpenChange={open => {
        if (!open) closeCloneDialog();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Clone Templates</DialogTitle>
          <DialogDescription>
            Copy all templates from{' '}
            <span className="text-foreground font-medium">{sourceForm?.displayName}</span> into
            another empty application form.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Select
            value={destinationFormId}
            onValueChange={setDestinationFormId}
            disabled={!hasDestinations || isCloning}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a destination form" />
            </SelectTrigger>
            <SelectContent>
              {destinationForms.map(form => (
                <SelectItem
                  key={form.id}
                  value={form.id}
                >
                  {form.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasDestinations ? (
            <Alert>
              <InfoIcon />
              <AlertDescription>
                The destination form must be empty. If it already has templates, the clone will be
                blocked.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <InfoIcon />
              <AlertDescription>
                No other {sourceForm?.product} application forms are available to clone into.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={closeCloneDialog}
            disabled={isCloning}
          >
            Cancel
          </Button>
          <Button
            onClick={() => cloneTemplates()}
            disabled={!destinationFormId || isCloning}
          >
            {isCloning ? 'Cloning...' : 'Clone Templates'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
